<template>
  <AppShell wide>
    <div class="page">
      <div class="topbar">
        <router-link to="/" class="back">返回查询</router-link>
        <h1>对比</h1>
        <label v-if="cards.length >= 2" class="diff-toggle">
          <input v-model="onlyDiff" type="checkbox" />
          只看差异
        </label>
      </div>

      <p v-if="cards.length < 2" class="empty">
        请先在查询结果里加入至少 2 款卡，再来对比。
        <router-link to="/">去查询</router-link>
      </p>

      <template v-else>
        <p v-if="hasLocked" class="preview-note">
          有卡片仍是未登录预览，这里只对比当前可见参数。登录后重新查询并加入，才能对比完整数据。
        </p>
        <div class="sheet-wrap">
          <div class="sheet" :style="{ '--cols': String(cards.length) }">
            <div class="corner"></div>
            <div v-for="(card, index) in cards" :key="card.id" class="col-head">
              <div class="head-tools">
                <label class="switch">
                  <span>切换</span>
                  <select :value="String(card.id)" @change="swapColumn(index, $event.target.value)">
                    <option v-for="option in cards" :key="option.id" :value="String(option.id)">
                      {{ option.name }}
                    </option>
                  </select>
                </label>
                <button type="button" class="remove" @click="removeColumn(card.id)">移除</button>
              </div>
              <h2>{{ card.name }}</h2>
              <div v-if="card.metrics" class="badges">
                <span v-if="card.metrics.is_domestic" class="badge on">国产</span>
                <span class="badge">{{ card.metrics?.memory_gb != null ? card.metrics.memory_gb + 'GB' : '—' }}</span>
                <span class="badge">{{ card.metrics?.fp16_tflops != null ? 'FP16 ' + card.metrics.fp16_tflops + 'T' : '—' }}</span>
                <span class="badge">{{ card.metrics?.tdp_w != null ? card.metrics.tdp_w + 'W' : '—' }}</span>
              </div>
            </div>

            <div v-for="item in sheetItems" :key="item.key" class="sheet-item">
              <h3 v-if="item.kind === 'section'" class="section">{{ item.title }}</h3>
              <template v-else>
                <div class="label">{{ item.label }}</div>
                <div
                  v-for="card in cards"
                  :key="item.key + '-' + card.id"
                  class="cell"
                  :class="{ diff: item.diff }"
                >{{ item.values[card.id] || '—' }}</div>
              </template>
            </div>
          </div>
        </div>
        <p v-if="onlyDiff && !sections.length" class="empty">当前可见参数没有差异。</p>
      </template>
    </div>
  </AppShell>
</template>

<script>
import AppShell from '../layouts/AppShell.vue'
import { readTray, removeTrayCard, writeTray } from '../utils/compareTray'

function norm(value) {
  if (value == null) return ''
  const text = String(value).trim()
  if (!text || text === '—') return ''
  return text
}

function rowOf(key, label, cards, pick) {
  const values = {}
  cards.forEach((card) => {
    const value = pick(card)
    values[card.id] = value == null || value === '' ? '—' : String(value)
  })
  const uniq = new Set(Object.values(values).map(norm))
  return { key, label, values, diff: uniq.size > 1 }
}

function priceText(card) {
  const prices = card.prices || []
  if (!prices.length) return '—'
  return prices
    .map((price) => {
      const bits = []
      if (price.msrp) bits.push(`官方 ${price.msrp}${price.currency ? ` ${price.currency}` : ''}`)
      if (price.market_price) bits.push(`市场 ${price.market_price}${price.currency ? ` ${price.currency}` : ''}`)
      if (price.secondhand_price) bits.push(`二手 ${price.secondhand_price}${price.currency ? ` ${price.currency}` : ''}`)
      const name = price.variant_name && price.variant_name !== card.name ? `${price.variant_name} ` : ''
      return name + (bits.join(' · ') || '—')
    })
    .join('\n')
}

export default {
  name: 'CompareWorkspace',
  components: { AppShell },
  data() {
    return {
      cards: [],
      onlyDiff: false
    }
  },
  computed: {
    hasLocked() {
      return this.cards.some((card) => card.locked)
    },
    sections() {
      const cards = this.cards
      if (cards.length < 2) return []
      const keep = (rows) => rows.filter((row) => (this.onlyDiff ? row.diff : true))
      const overview = keep([
        rowOf('vendor', '厂商', cards, (card) => card.metrics?.vendor || '—'),
        rowOf('domestic', '国产', cards, (card) => (card.metrics?.is_domestic ? '是' : '否')),
        rowOf('memory', '显存 (GB)', cards, (card) => card.metrics?.memory_gb ?? '—'),
        rowOf('fp16', 'FP16 (T)', cards, (card) => card.metrics?.fp16_tflops ?? '—'),
        rowOf('tdp', '功耗 (W)', cards, (card) => card.metrics?.tdp_w ?? '—')
      ])
      const labels = []
      cards.forEach((card) => {
        const attrs = card.attrs || []
        attrs.forEach((attr) => {
          if (attr.field_label && !labels.includes(attr.field_label)) labels.push(attr.field_label)
        })
      })
      const specs = keep(
        labels.map((label) =>
          rowOf(`attr:${label}`, label, cards, (card) => {
            const hit = (card.attrs || []).find((attr) => attr.field_label === label)
            return hit ? hit.field_value : '—'
          })
        )
      )
      const sections = []
      if (overview.length) sections.push({ title: '概览', rows: overview })
      if (specs.length) sections.push({ title: '参数', rows: specs })
      if (cards.some((card) => (card.prices || []).length)) {
        const prices = keep([rowOf('price', '参考价格', cards, priceText)])
        if (prices.length) sections.push({ title: '价格', rows: prices })
      }
      return sections
    },
    sheetItems() {
      const items = []
      this.sections.forEach((section) => {
        items.push({ kind: 'section', key: `section:${section.title}`, title: section.title })
        section.rows.forEach((row) => {
          items.push({ kind: 'row', ...row })
        })
      })
      return items
    }
  },
  mounted() {
    this.cards = this.orderedTray()
  },
  methods: {
    orderedTray() {
      const tray = readTray()
      const ids = String(this.$route.query.ids || '')
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean)
      if (!ids.length) return tray
      const map = new Map(tray.map((card) => [String(card.id), card]))
      const ordered = ids.map((id) => map.get(id)).filter(Boolean)
      const rest = tray.filter((card) => !ids.includes(String(card.id)))
      return [...ordered, ...rest]
    },
    syncQuery() {
      const ids = this.cards.map((card) => card.id).join(',')
      this.$router.replace({ path: '/compare', query: ids ? { ids } : {} })
    },
    swapColumn(index, rawId) {
      const other = this.cards.findIndex((card) => String(card.id) === String(rawId))
      if (other < 0 || other === index) return
      const next = this.cards.slice()
      const current = next[index]
      next[index] = next[other]
      next[other] = current
      this.cards = next
      writeTray(next)
      this.syncQuery()
    },
    removeColumn(id) {
      this.cards = removeTrayCard(id)
      this.syncQuery()
    }
  }
}
</script>

<style scoped>
.page { padding-bottom: 24px; }
.topbar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 18px;
}
.back { color: #165dff; text-decoration: none; font-size: 14px; white-space: nowrap; }
.back:hover { text-decoration: underline; }
h1 { margin: 0; font-size: 34px; font-weight: 600; letter-spacing: -0.03em; flex: 1; }
.diff-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #4e5969;
  white-space: nowrap;
}
.empty { color: #86909c; padding: 28px 0; }
.empty a { margin-left: 8px; color: #165dff; }
.preview-note {
  margin: 0 0 14px;
  font-size: 13px;
  color: #4e5969;
  background: #fff7e8;
  border: 1px solid #ffe4ba;
  border-radius: 12px;
  padding: 8px 12px;
}
.sheet-wrap {
  overflow: auto;
  max-height: calc(100vh - 150px);
  border: 1px solid #e5e6eb;
  border-radius: 12px;
  background: #fff;
}
.sheet {
  display: grid;
  grid-template-columns: 148px repeat(var(--cols), minmax(210px, 1fr));
  min-width: calc(148px + var(--cols) * 210px);
}
.sheet-item { display: contents; }
.corner, .col-head, .label, .cell, .section {
  border-bottom: 1px solid #f2f3f5;
}
.corner, .col-head {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
}
.corner { left: 0; z-index: 3; }
.label {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #fafbfc;
  color: #4e5969;
  font-size: 13px;
  font-weight: 500;
  padding: 12px 14px;
}
.col-head { padding: 16px 16px 14px; }
.head-tools { display: flex; gap: 8px; align-items: center; margin-bottom: 10px; }
.switch { display: flex; flex-direction: column; gap: 4px; flex: 1; min-width: 0; font-size: 11px; color: #86909c; }
.switch select {
  width: 100%;
  height: 32px;
  border: 1px solid #e5e6eb;
  border-radius: 12px;
  background: #fff;
  color: #1d2129;
  font-size: 13px;
  padding: 0 8px;
}
.remove {
  border: 0;
  background: transparent;
  color: #86909c;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
  align-self: flex-end;
  height: 32px;
}
.remove:hover { color: #d3382c; }
.col-head h2 { margin: 0; font-size: 18px; line-height: 1.35; font-weight: 600; }
.badges { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.badge {
  font-size: 12px;
  color: #4e5969;
  background: #f2f3f5;
  border-radius: 12px;
  padding: 2px 8px;
}
.badge.on { color: #165dff; background: #e8f3ff; font-weight: 600; }
.section {
  grid-column: 1 / -1;
  margin: 0;
  padding: 14px 16px 8px;
  font-size: 13px;
  font-weight: 600;
  color: #1d2129;
  background: #f7f8fa;
}
.cell {
  padding: 12px 16px;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-line;
  color: #1d2129;
}
.cell.diff { background: #e8f3ff; }
@media (max-width: 720px) {
  .topbar { flex-wrap: wrap; }
  h1 { font-size: 22px; flex-basis: 100%; order: -1; }
}
</style>
